import React, { useState, useMemo, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import loginTrackingService from '../services/loginTrackingService';

const Administration = ({ userRole, roleChecking }) => {
  const { accounts } = useMsal();
  const account = accounts[0];

  const [selectedMonth, setSelectedMonth] = useState('all');
  const [allLogins, setAllLogins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check admin access — only calendarEditors
  const isAdmin = userRole === 'editor';

  // Fetch login data from Dataverse
  useEffect(() => {
    if (!isAdmin) return;
    const fetchLogins = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await loginTrackingService.fetchAllLogins();
        setAllLogins(data);
      } catch (err) {
        console.error('Failed to load login data:', err);
        setError('Failed to load login data from Dataverse.');
      } finally {
        setLoading(false);
      }
    };
    fetchLogins();
  }, [isAdmin]);

  const monthlySummary = useMemo(() => loginTrackingService.getMonthlySummary(allLogins), [allLogins]);
  const userSummary = useMemo(() => loginTrackingService.getUserSummary(allLogins), [allLogins]);

  const filteredLogins = useMemo(() => {
    if (selectedMonth === 'all') return allLogins;
    const [year, month] = selectedMonth.split('-').map(Number);
    return loginTrackingService.getLoginsByMonth(allLogins, year, month - 1);
  }, [selectedMonth, allLogins]);

  const handleExport = () => {
    loginTrackingService.exportCSV(filteredLogins);
  };

  if (roleChecking) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontSize: '16px', color: '#6b7280' }}>Checking permissions...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '32px', maxWidth: '480px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#991b1b', marginBottom: '8px' }}>Access Denied</h2>
          <p style={{ color: '#dc2626' }}>You do not have administrator permissions to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Header */}
      <header style={{ backgroundColor: '#1e293b', borderBottom: '3px solid #3b82f6', padding: '16px 32px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#ffffff', margin: 0 }}>Administration</h1>
            <p style={{ fontSize: '14px', color: '#94a3b8', margin: '4px 0 0' }}>Login Analytics &amp; Reporting</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: '14px', color: '#94a3b8', margin: 0 }}>Signed in as</p>
            <p style={{ fontSize: '14px', fontWeight: '500', color: '#ffffff', margin: 0 }}>{account?.name}</p>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 32px' }}>
        {/* Loading / Error states */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
            <p style={{ fontSize: '16px' }}>Loading login data from Dataverse...</p>
          </div>
        )}

        {error && (
          <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
            <p style={{ color: '#dc2626', margin: 0 }}>{error}</p>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <SummaryCard label="Total Logins" value={allLogins.length} color="#3b82f6" />
              <SummaryCard label="Unique Users" value={userSummary.length} color="#10b981" />
              <SummaryCard label="Months Tracked" value={monthlySummary.length} color="#8b5cf6" />
              <SummaryCard
                label="Avg Logins / Month"
                value={monthlySummary.length > 0 ? Math.round(allLogins.length / monthlySummary.length) : 0}
                color="#f59e0b"
              />
            </div>

            {/* Controls */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#ffffff',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                <option value="all">All Months</option>
                {monthlySummary.map((m) => (
                  <option key={m.month} value={m.month}>
                    {m.label}
                  </option>
                ))}
              </select>

              <button
                onClick={handleExport}
                disabled={filteredLogins.length === 0}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: filteredLogins.length > 0 ? '#3b82f6' : '#9ca3af',
                  color: '#ffffff',
                  fontSize: '14px',
                  cursor: filteredLogins.length > 0 ? 'pointer' : 'not-allowed',
                  fontWeight: '500',
                }}
              >
                Export CSV
              </button>
            </div>

            {/* Monthly Breakdown + User Summary */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              {/* Monthly Chart Table */}
              <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginTop: 0, marginBottom: '16px' }}>Monthly Breakdown</h3>
                {monthlySummary.length === 0 ? (
                  <p style={{ color: '#6b7280', fontSize: '14px' }}>No login data recorded yet.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ textAlign: 'left', padding: '8px', fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>Month</th>
                        <th style={{ textAlign: 'right', padding: '8px', fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>Total Logins</th>
                        <th style={{ textAlign: 'right', padding: '8px', fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>Unique Users</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlySummary.map((m) => (
                        <tr key={m.month} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '8px', fontSize: '14px', color: '#1f2937' }}>{m.label}</td>
                          <td style={{ padding: '8px', fontSize: '14px', color: '#1f2937', textAlign: 'right', fontWeight: '600' }}>{m.totalLogins}</td>
                          <td style={{ padding: '8px', fontSize: '14px', color: '#1f2937', textAlign: 'right' }}>{m.uniqueUsers}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* User Summary */}
              <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginTop: 0, marginBottom: '16px' }}>User Summary</h3>
                {userSummary.length === 0 ? (
                  <p style={{ color: '#6b7280', fontSize: '14px' }}>No users recorded yet.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ textAlign: 'left', padding: '8px', fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>User</th>
                        <th style={{ textAlign: 'right', padding: '8px', fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>Logins</th>
                        <th style={{ textAlign: 'right', padding: '8px', fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>Last Login</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userSummary.map((user) => (
                        <tr key={user.email} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '8px' }}>
                            <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>{user.name}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>{user.email}</div>
                          </td>
                          <td style={{ padding: '8px', fontSize: '14px', color: '#1f2937', textAlign: 'right', fontWeight: '600' }}>{user.loginCount}</td>
                          <td style={{ padding: '8px', fontSize: '13px', color: '#6b7280', textAlign: 'right' }}>
                            {new Date(user.lastLogin).toLocaleDateString('en-GB')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Detailed Login Log */}
            <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '20px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginTop: 0, marginBottom: '16px' }}>
                Login Log {selectedMonth !== 'all' && <span style={{ fontWeight: '400', color: '#6b7280' }}>— {monthlySummary.find((m) => m.month === selectedMonth)?.label}</span>}
                <span style={{ fontWeight: '400', fontSize: '14px', color: '#6b7280', marginLeft: '8px' }}>({filteredLogins.length} records)</span>
              </h3>
              {filteredLogins.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: '14px' }}>No login records for the selected period.</p>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#ffffff' }}>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ textAlign: 'left', padding: '8px', fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>Name</th>
                        <th style={{ textAlign: 'left', padding: '8px', fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>Email</th>
                        <th style={{ textAlign: 'left', padding: '8px', fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>Date &amp; Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLogins.map((entry, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '8px', fontSize: '14px', color: '#1f2937' }}>{entry.name}</td>
                          <td style={{ padding: '8px', fontSize: '14px', color: '#6b7280' }}>{entry.email}</td>
                          <td style={{ padding: '8px', fontSize: '14px', color: '#6b7280' }}>
                            {new Date(entry.timestamp).toLocaleString('en-GB')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

const SummaryCard = ({ label, value, color }) => (
  <div
    style={{
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      borderTop: `3px solid ${color}`,
      padding: '20px',
    }}
  >
    <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 4px', fontWeight: '500' }}>{label}</p>
    <p style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', margin: 0 }}>{value}</p>
  </div>
);

export default Administration;
